import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useController, useFormContext } from 'react-hook-form';
import { getDeviceSelectionCategoryGroups } from '../../../common/utils/deviceSelectionCategoryUtilsCoCo.js';
import type { DeviceSelectionCategoryCoCo } from '../../../common/enums.js';
import type { DeviceSelectionProductTypeCoCo } from '../../../common/utils/deviceListTabsUtilsCoCo.js';
import type { DeviceSelectionCategoryConfigCoCo } from '../../../common/utils/deviceSelectionCategoryUtilsCoCo.js';

import './DeviceSelectionCategoriesCoCo.scss';

const SELECTED_COLOR = 'blue-600';

interface DeviceSelectionCategoryBoxCoCoProps {
  config: DeviceSelectionCategoryConfigCoCo;
  name: string;
  selected: boolean;
  onSelect: () => void;
}

// A box with a visually hidden native radio, so that keyboard and screen reader support comes from the browser
const DeviceSelectionCategoryBoxCoCo = ({
  config: { category, icon, label },
  name,
  selected,
  onSelect,
}: DeviceSelectionCategoryBoxCoCoProps) => (
  <label
    className={classNames('of-device-selection-categories-coco__category', {
      ['of-device-selection-categories-coco__category--selected']: selected,
    })}
  >
    <input
      type="radio"
      className="of-device-selection-categories-coco__input"
      name={name}
      value={category}
      checked={selected}
      onChange={onSelect}
    />
    <CL.Icon icon={icon} type="regular" size="l" color={selected ? SELECTED_COLOR : undefined} aria-hidden="true" />
    <span className="of-device-selection-categories-coco__label">{label()}</span>
  </label>
);

export interface DeviceSelectionCategoriesCoCoProps {
  name: string;
  productType?: DeviceSelectionProductTypeCoCo;
}

interface DeviceSelectionCategorySelectorCoCoProps extends DeviceSelectionCategoriesCoCoProps {
  value?: DeviceSelectionCategoryCoCo;
  onChange: (category: DeviceSelectionCategoryCoCo) => void;
}

export const DeviceSelectionCategorySelectorCoCo = ({
  name,
  productType,
  value,
  onChange,
}: DeviceSelectionCategorySelectorCoCoProps) => {
  const groups = getDeviceSelectionCategoryGroups(productType);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="of-device-selection-categories-coco">
      {groups.map(({ id, heading, categories }) => {
        const headingId = `${name}-${id}-heading`;
        return (
          <section key={id} className="of-device-selection-categories-coco__group">
            <h3 id={headingId} className="of-device-selection-categories-coco__heading">
              {heading()}
            </h3>
            <div
              className="of-device-selection-categories-coco__categories"
              role="radiogroup"
              aria-labelledby={headingId}
            >
              {categories.map(config => (
                <DeviceSelectionCategoryBoxCoCo
                  key={config.category}
                  config={config}
                  name={name}
                  selected={value === config.category}
                  onSelect={() => onChange(config.category)}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export const DeviceSelectionCategoriesCoCo = ({ name, productType }: DeviceSelectionCategoriesCoCoProps) => {
  const { control } = useFormContext();
  const {
    field: { onChange, value },
  } = useController({ name, control });

  return (
    <DeviceSelectionCategorySelectorCoCo name={name} productType={productType} value={value} onChange={onChange} />
  );
};
